<template>
  <div class="idp-wrap">

    <!-- ══ HEADER ══════════════════════════════════════════ -->
    <div class="idp-header-section">

      <!-- badges row -->
      <div class="idp-meta-top">
        <span class="idp-ref-badge" v-if="issueData.reference_num">
          {{ issueData.reference_num }}
        </span>
        <span
          v-if="issueData.priority"
          class="idp-badge"
          :class="`idp-pri--${issueData.priority}`"
        >
          <v-icon size="10">mdi-flag</v-icon>
          {{ issueData.priority }}
        </span>
        <span v-if="issueData.issue_type_name" class="idp-badge idp-badge--type">
          {{ issueData.issue_type_name }}
        </span>
        <span v-if="issueData.stage" class="idp-badge" :class="`idp-stage--${issueData.stage}`">
          <v-icon size="10">mdi-circle</v-icon>
          {{ stageName || issueData.stage }}
        </span>
        <span v-if="issueData.qa_required" class="idp-badge idp-badge--qa">
          <v-icon size="10">mdi-check-decagram</v-icon> QA Required
        </span>
        <span v-if="issueData.qc_required" class="idp-badge idp-badge--qc">
          <v-icon size="10">mdi-shield-check</v-icon> QC Required
        </span>
      </div>

      <!-- title -->
      <h2 class="idp-title">{{ issueData.name || '—' }}</h2>

      <!-- one-liner -->
      <p class="idp-one-line" v-if="issueData.one_line_comment">
        {{ issueData.one_line_comment }}
      </p>
    </div>

    <div class="idp-divider"></div>

    <!-- ══ DETAIL GRID ══════════════════════════════════════ -->
    <div class="idp-detail-section">
      <v-row class="ma-0">
        <v-col
          v-for="(item, i) in detailItems"
          :key="i"
          cols="12"
          sm="6"
          class="pa-1"
        >
          <div class="idp-detail-card">
            <div class="idp-detail-icon" :style="{ background: item.bg }">
              <v-icon size="14" :color="item.color">{{ item.icon }}</v-icon>
            </div>
            <div class="idp-detail-body">
              <span class="idp-detail-label">{{ item.label }}</span>
              <span class="idp-detail-value" :class="item.cls">
                {{ toProperCase(item.value) }}
                <span v-if="item.badge" class="idp-overdue-pip">{{ item.badge }}</span>
              </span>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <div class="idp-divider" v-if="issueData.description"></div>

    <!-- ══ DESCRIPTION ══════════════════════════════════════ -->
    <div class="idp-desc-section" v-if="issueData.description">
      <div class="idp-section-label">
        <v-icon size="13" color="#6366f1">mdi-text-box-outline</v-icon>
        Description
      </div>
      <div
        class="idp-desc-body"
        :class="{ 'idp-desc-body--expanded': showFullDesc }"
        v-html="truncatedDescription"
      ></div>
      <button
        v-if="descriptionTooLong"
        class="idp-read-more"
        @click="showFullDesc = !showFullDesc"
      >
        <v-icon size="13">{{ showFullDesc ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        {{ showFullDesc ? 'Show less' : 'Read more' }}
      </button>
    </div>

  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'IssueDetailPanel',

  components: {},

  props: {
    issueData: { type: Object, default: () => ({}) },
    stageName: { type: String, default: '' },
  },

  data() {
    return { showFullDesc: false };
  },

  methods: {
    toProperCase(str) {
      if (!str) return '';
      return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
  },

  computed: {
    /* Compute isOverdue here — no need to pass as a prop */
    isOverdue() {
      return !!(this.issueData.end_date &&
        moment(this.issueData.end_date).isBefore(moment()));
    },

    detailItems() {
      const d   = this.issueData;
      const fmt = v => moment(v).format('DD MMM YYYY, hh:mm A');

      const items = [
        {
          icon: 'mdi-domain', color: '#6366f1', bg: '#eef2ff',
          label: 'Account', value: d.account_name,
        },
        {
          icon: 'mdi-briefcase-outline', color: '#8b5cf6', bg: '#f5f3ff',
          label: 'Project', value: d.project_name,
        },
        {
          icon: 'mdi-tag-outline', color: '#0ea5e9', bg: '#f0f9ff',
          label: 'Category', value: d.category_name,
        },
        {
          icon: 'mdi-tag-multiple-outline', color: '#0ea5e9', bg: '#f0f9ff',
          label: 'Sub Category', value: d.sub_category_name,
        },
        {
          icon: 'mdi-account-arrow-right', color: '#10b981', bg: '#ecfdf5',
          label: 'Next Action', value: d.next_action_name,
        },
        {
          /* API returns submitted_name for the resolved display name */
          icon: 'mdi-account-edit-outline', color: '#f59e0b', bg: '#fffbeb',
          label: 'Submitted By', value: d.submitted_name,
        },
        {
          icon: 'mdi-clipboard-list-outline', color: '#64748b', bg: '#f8fafc',
          label: 'Task', value: d.task_name,
        },
        {
          icon: 'mdi-currency-usd', color: '#64748b', bg: '#f8fafc',
          label: 'Customer', value: d.customer_name,
        },
        {
          icon: 'mdi-timer-outline', color: '#6366f1', bg: '#eef2ff',
          label: 'Estimate',
          value: d.estimate
            ? (d.estimate >= 60
                ? `${Math.floor(d.estimate / 60)}h ${d.estimate % 60}m`
                : `${d.estimate}m`)
            : null,
        },
        {
          icon:  'mdi-calendar-clock',
          color: this.isOverdue ? '#ef4444' : '#64748b',
          bg:    this.isOverdue ? '#fef2f2' : '#f8fafc',
          label: 'Due Date',
          value: d.end_date ? fmt(d.end_date) : null,
          badge: this.isOverdue ? 'Overdue' : null,
          cls:   this.isOverdue ? 'idp-val--overdue' : '',
        },
        {
          icon: 'mdi-calendar-plus-outline', color: '#64748b', bg: '#f8fafc',
          label: 'Created', value: d.created ? fmt(d.created) : null,
        },
        {
          icon: 'mdi-calendar-edit', color: '#64748b', bg: '#f8fafc',
          label: 'Modified', value: d.modified ? fmt(d.modified) : null,
        },
      ];

      /* Only show items that have a non-empty value */
      return items.filter(it => it.value != null && it.value !== '');
    },

    descriptionTooLong() {
      return (this.issueData.description || '').replace(/<[^>]+>/g, '').length > 300;
    },

    truncatedDescription() {
      if (this.showFullDesc || !this.descriptionTooLong) {
        return this.issueData.description;
      }
      return (this.issueData.description || '')
        .replace(/<[^>]+>/g, '')
        .substring(0, 300) + '…';
    },
  },
};
</script>

<style scoped>
/* ── SCROLL CONTAINER ─────────────────────────────────── */
.idp-wrap {
  display: flex;
  flex-direction: column;
  gap: 0;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 28px;
  background: #fff;
  box-sizing: border-box;
}
.idp-wrap::-webkit-scrollbar { width: 5px; }
.idp-wrap::-webkit-scrollbar-track { background: transparent; }
.idp-wrap::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 8px; }

/* ── HEADER ───────────────────────────────────────────── */
.idp-header-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 4px;
}
.idp-meta-top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

/* ref badge */
.idp-ref-badge {
  font-size: 12px; font-weight: 800; color: #6366f1;
  background: #eef2ff; border: 1.5px solid #c7d2fe;
  padding: 3px 12px; border-radius: 99px;
  letter-spacing: 0.5px; white-space: nowrap;
}

/* generic badge */
.idp-badge {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 700;
  padding: 3px 10px; border-radius: 99px;
  text-transform: capitalize; white-space: nowrap;
}
.idp-badge--type { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.idp-badge--qa   { background: #fef9c3; color: #a16207; border: 1px solid #fde047; }
.idp-badge--qc   { background: #e0f2fe; color: #0369a1; border: 1px solid #7dd3fc; }

/* priority colours */
.idp-pri--low      { background: #dcfce7; color: #15803d; }
.idp-pri--medium   { background: #fef9c3; color: #a16207; }
.idp-pri--high     { background: #fee2e2; color: #b91c1c; }
.idp-pri--critical { background: #fce7f3; color: #9d174d; }

/* stage colours */
.idp-stage--inprogress { background: #eef2ff; color: #6366f1; border: 1px solid #c7d2fe; }
.idp-stage--open       { background: #f0f9ff; color: #0ea5e9; border: 1px solid #bae6fd; }
.idp-stage--pending    { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
.idp-stage--closed     { background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; }
.idp-stage--completed  { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }

.idp-title {
  font-size: 20px; font-weight: 800; color: #0f172a;
  line-height: 1.35; margin: 0;
}
.idp-one-line {
  font-size: 13px; color: #64748b;
  font-style: italic; margin: 0; line-height: 1.5;
}

/* ── DIVIDER ──────────────────────────────────────────── */
.idp-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0 20%, #e2e8f0 80%, transparent);
  flex-shrink: 0;
  margin: 16px 0;
}

/* ── DETAIL CARDS ─────────────────────────────────────── */
.idp-detail-card {
  display: flex; align-items: flex-start; gap: 10px;
  background: #fafbff; border: 1px solid #f1f5f9; border-radius: 12px;
  padding: 10px 12px;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.idp-detail-card:hover {
  border-color: #c7d2fe;
  box-shadow: 0 2px 10px rgba(99,102,241,0.08);
  transform: translateY(-1px);
}
.idp-detail-icon {
  width: 28px; height: 28px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; margin-top: 1px;
}
.idp-detail-body {
  display: flex; flex-direction: column; gap: 2px; min-width: 0;
}
.idp-detail-label {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.7px; color: #94a3b8; line-height: 1.2;
}
.idp-detail-value {
  font-size: 13px; font-weight: 600; color: #1e293b;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  line-height: 1.4;
}
.idp-val--overdue { color: #ef4444; }
.idp-overdue-pip {
  display: inline-block; font-size: 9px;
  background: #fee2e2; color: #ef4444;
  padding: 1px 6px; border-radius: 99px;
  font-weight: 700; margin-left: 4px; vertical-align: middle;
}

/* ── DESCRIPTION ──────────────────────────────────────── */
.idp-desc-section { display: flex; flex-direction: column; gap: 8px; }
.idp-section-label {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.7px; color: #64748b;
}
.idp-desc-body {
  font-size: 13px; color: #475569; line-height: 1.75;
  background: #f8fafc; border: 1px solid #f1f5f9; border-radius: 12px;
  padding: 14px 16px; max-height: 160px; overflow: hidden;
  transition: max-height 0.35s ease;
}
.idp-desc-body--expanded { max-height: 480px; overflow-y: auto; }
.idp-desc-body::-webkit-scrollbar { width: 4px; }
.idp-desc-body::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.07); border-radius: 8px; }
.idp-read-more {
  display: inline-flex; align-items: center; gap: 4px; align-self: flex-start;
  font-size: 12px; font-weight: 600; color: #6366f1;
  background: none; border: none; cursor: pointer; padding: 0;
}
.idp-read-more:hover { text-decoration: underline; }
</style>
